import { useMemo, useState, useEffect } from 'react';
import { getStoreRankDetailsCoreUIData } from '../utils/dataTransfer';
import { RANK_TYPES_NAMES_MAPPING } from '@/pages/intelligence/_config/storeRank/config';

import type { IStoreRankDetailsResData } from '@/pages/intelligence/types/storeRank';
import type {
  IStoreRankDetailsVisualisSourceInfoOption,
  IStoreRankDetailsTablesSourceInfoOption,
} from '../types';

const defaultRankTablesDimensionsNameFormatter = (dimension: string) => dimension;

const defaultSectionalRankVisualisDimensionsNameFormatter = (dimension: string) =>
  RANK_TYPES_NAMES_MAPPING[dimension] || dimension;

const defaultSectionalRankTablesDimensionsNameFormatter = (dimension: string) => {
  const dimensionName = RANK_TYPES_NAMES_MAPPING[dimension];
  return dimensionName ? `#${dimensionName}` : dimension;
};

export default function useStoreRankDetailsCoreUI(option: {
  isLoading: boolean;
  isSectionalRank: boolean; // 是否为分段排名 即 显示为 1-3、4-10、11-50、51-200 这种形式
  // 是否开启表格折叠交互
  isExpandable?: boolean;
  visualisHelpperOptionConfig?: {
    isShowYAxisName?: boolean;
    yAxisName?: string;
    yAxisNamePadding?: null | number[];
    tooltipHeaderLeftTitle?: string;
    tooltipHeaderLeftIcon?: string;
    rankDimensionsNameFormatter?: (name: string) => string;
    sectionalRankDimensionsNameFormatter?: (name: string) => string;
  };
  tablesHelpperOptionConfig?: {
    rankDimensionsNameFormatter?: (name: string) => string;
    sectionalRankDimensionsNameFormatter?: (name: string) => string;
  };
  data: any[];
}) {
  const {
    isLoading,
    isSectionalRank,
    isExpandable,
    visualisHelpperOptionConfig,
    tablesHelpperOptionConfig,
    data,
  } = option || {};

  const {
    isShowYAxisName,
    yAxisName,
    yAxisNamePadding,
    tooltipHeaderLeftTitle,
    tooltipHeaderLeftIcon,
    rankDimensionsNameFormatter: rankVisualisDimensionsNameFormatter,
    sectionalRankDimensionsNameFormatter:
      sectionalRankVisualisDimensionsNameFormatter = defaultSectionalRankVisualisDimensionsNameFormatter,
  } = visualisHelpperOptionConfig ?? {};

  const {
    rankDimensionsNameFormatter:
      rankTablesDimensionsNameFormatter = defaultRankTablesDimensionsNameFormatter,
    sectionalRankDimensionsNameFormatter:
      sectionalRankTablesDimensionsNameFormatter = defaultSectionalRankTablesDimensionsNameFormatter,
  } = tablesHelpperOptionConfig ?? {};

  const [visualisCoreOption, setVisualisCoreOption] =
    useState<IStoreRankDetailsVisualisSourceInfoOption>({
      sourceData: [],
      sourceDataDimensions: [],
    });

  const visualisHelpperOption = useMemo(
    () => ({
      isAreaEChart: isSectionalRank,
      isShowYAxisName,
      yAxisName,
      yAxisNamePadding,
      isYAxisInverse: !isSectionalRank,
      tooltipHeaderLeftTitle,
      tooltipHeaderLeftIcon,
      sourceDataDimensionNameFormatter: isSectionalRank
        ? sectionalRankVisualisDimensionsNameFormatter
        : rankVisualisDimensionsNameFormatter,
    }),
    [
      isSectionalRank,
      isShowYAxisName,
      yAxisName,
      yAxisNamePadding,
      tooltipHeaderLeftTitle,
      tooltipHeaderLeftIcon,
      rankVisualisDimensionsNameFormatter,
      sectionalRankVisualisDimensionsNameFormatter,
    ],
  );

  const visualisOption = useMemo(
    () => ({ ...visualisCoreOption, ...visualisHelpperOption }),
    [visualisCoreOption, visualisHelpperOption],
  );

  const [tablesCoreOption, setTablesCoreOption] = useState<IStoreRankDetailsTablesSourceInfoOption>(
    {
      sourceDataKey: 'date',
      sourceData: [],
      sourceDataDimensions: [],
    },
  );

  const tablesHelpperOption = useMemo(
    () => ({
      showRankName: isSectionalRank,
      isExpandable: isExpandable ?? isSectionalRank,
      sourceDataDimensionNameFormatter: isSectionalRank
        ? sectionalRankTablesDimensionsNameFormatter
        : rankTablesDimensionsNameFormatter,
    }),
    [
      isSectionalRank,
      rankTablesDimensionsNameFormatter,
      sectionalRankTablesDimensionsNameFormatter,
    ],
  );

  const tablesOption = useMemo(
    () => ({ ...tablesCoreOption, ...tablesHelpperOption }),
    [tablesCoreOption, tablesHelpperOption],
  );

  useEffect(() => {
    if (isLoading) return;
    const { sourceDataDimensions, visualisSourceData, tablesSourceData } =
      getStoreRankDetailsCoreUIData(data);
    setVisualisCoreOption({
      sourceDataDimensions,
      sourceData: visualisSourceData,
    });
    setTablesCoreOption({
      sourceDataDimensions,
      sourceData: tablesSourceData,
    });
  }, [isLoading, isSectionalRank, data]);

  return {
    visualisOption,
    tablesOption,
  };
}
